/**
 * Module dependencies.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Button } from 'src/components/core/buttons/button';
import { ButtonWrapper, Wrapper } from './steps';
import { FormData, useSchedule } from 'src/providers/schedule';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { axiosInstance } from 'src/core/utils/requests';
import { breakpoints } from '@untile/react-core/styles/breakpoints';
import { formatDate } from 'src/core/utils/date';
import { parseCookies } from 'nookies';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCallback, useState } from 'react';
import { useGTMEvent } from 'src/hooks/use-gtm-event';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'next-i18next';
import arrowRightSvg from 'src/assets/svgs/arrow-right.svg';
import calendarSvg from 'src/assets/svgs/calendar-xs.svg';
import clockSvg from 'src/assets/svgs/clock.svg';
import editSvg from 'src/assets/svgs/edit-xs.svg';
import infoSvg from 'src/assets/svgs/info.svg';
import styled from 'styled-components';

/**
 * `Card` styled component.
 */

const Card = styled(motion.div)`
  background-color: ${transparentize('cyan800', 0.16)};
  border-radius: var(--border-radius-xs);
  display: grid;
  grid-gap: var(--space-sm);
  height: min-content;
  padding: var(--space-sm) var(--space-xs);
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text).attrs({ as: 'p', variant: 'labelS' })`
  color: var(--color-slate300);
  margin-bottom: var(--space-xxs);
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: var(--space-xxs);
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ as: 'p', variant: 'bodyL' })`
  color: var(--color-slate50);
`;

/**
 * `Info` styled component.
 */

const Info = styled.div`
  color: var(--color-slate300);
  display: grid;
  gap: var(--space-xxs);
  grid-template-columns: min-content 1fr;
`;

/**
 * Export `UserInfoConfirm` styled component.
 */

export const UserInfoConfirm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation('schedule');
  const { formData, isSantanderFlow, setProcessCode, setStep } = useSchedule();

  const [channelId, setChannelId] = useState<string>('2');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cookies = parseCookies();
  const sendGTMEvent = useGTMEvent();

  if (cookies.canal && cookies.canal !== 'santander' && !isSantanderFlow) {
    axiosInstance
      .post('/get-radar-channel-code', { canal: cookies.canal })
      .then(({ data }) => {
        setChannelId(data?.data?.ItemID);
      })
      .catch(() => {
        setChannelId('2');
      });
  }

  const normalizedTimeFrame = useCallback(() => {
    const [start, end] = formData?.timeFrame?.split('-') || [];

    return {
      end: `${formData?.date} ${end.split('h')[0]}:00:00`,
      start: `${formData?.date} ${start.split('h')[0]}:00:00`
    };
  }, [formData?.date, formData?.timeFrame]);

  const handleOnSubmit = useCallback(
    async (formData: FormData) => {
      setIsSubmitting(true);

      const token = await executeRecaptcha?.('schedule');

      try {
        axiosInstance
          .post('/schedule', {
            channelId,
            countyCode: formData?.county?.countyCode,
            email: formData?.email || '',
            endDate: normalizedTimeFrame().end,
            fullName: `${formData?.name}`,
            isSantanderFlow,
            phone: formData?.phone?.replace(/\s/g, ''),
            startDate: normalizedTimeFrame().start,
            token
          })
          .then(({ data }) => {
            const processCode = data?.data?.processCode;
            setProcessCode(processCode);

            sendGTMEvent({
              event: 'schedule_request',
              process_id: processCode // eslint-disable-line id-match
            });

            setTimeout(() => {
              setIsSubmitting(false);
              setStep(5);
            }, 1000);
          })
          .catch(() => {
            setIsSubmitting(false);
            setStep(6);
          });
      } catch (error) {
        setIsSubmitting(false);
        setStep(6);
      }
    },
    [channelId, executeRecaptcha, isSantanderFlow, normalizedTimeFrame, sendGTMEvent, setProcessCode, setStep]
  );

  const hasEmail = formData.email !== '';
  const isMobile = useBreakpoint(0, breakpoints.md - 1);

  const animationProps = {
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    initial: { opacity: 0 }
  };

  return (
    <>
      <Wrapper>
        <AnimatePresence mode={'wait'}>
          <Card {...animationProps}>
            <div>
              <Label>{t('labels.district')}</Label>

              <Description>{formData.district.districtName}</Description>
            </div>

            <div>
              <Label>{t('labels.county')}</Label>

              <Description>{formData.county.countyName}</Description>
            </div>
          </Card>

          <Card {...animationProps}>
            <div>
              <Label>{t('form.labels.dateHour')}</Label>

              <DescriptionWrapper style={{ gap: 'var(--space-sm)' }}>
                <DescriptionWrapper>
                  <Svg color={'var(--color-slate500)'} icon={calendarSvg} size={'20px'} />

                  <Description>{formatDate(formData.date)}</Description>
                </DescriptionWrapper>

                <DescriptionWrapper>
                  <Svg color={'var(--color-slate500)'} icon={clockSvg} size={'18px'} />

                  <Description>{formData.timeFrame}</Description>
                </DescriptionWrapper>
              </DescriptionWrapper>
            </div>
          </Card>

          <Card {...animationProps}>
            <div>
              <Label>{t('form.labels.name')}</Label>

              <Description>{formData.name}</Description>
            </div>

            <div>
              <Label>{t('form.labels.phone')}</Label>

              <Description>{formData.phone}</Description>
            </div>

            <div>
              <Label>
                {t('form.labels.email')} {t('common:forms.labels.optional')}
              </Label>

              <Description
                style={{
                  color: hasEmail ? 'var(--color-slate50)' : 'var(--color-slate300)',
                  opacity: hasEmail ? 1 : 0.5
                }}
              >
                {hasEmail ? formData.email : t('labels.emptyField')}
              </Description>
            </div>

            <Info>
              <Svg icon={infoSvg} size={'18px'} style={{ paddingTop: '2px' }} />

              <Text as={'p'} style={{ color: 'var(--color-slate50)' }} variant={'labelM'}>
                {t('form.descriptionAlternative')}
              </Text>
            </Info>

            {isMobile && (
              <ButtonWrapper>
                <Button
                  aria-label={t('common:actions.edit')}
                  colorTheme={'ghost'}
                  disabled={isSubmitting}
                  icon={editSvg}
                  onClick={() => setStep(2)}
                  reverse
                  size={'medium'}
                >
                  {t('common:actions.edit')}
                </Button>

                <Button
                  aria-label={t('common:actions.confirm')}
                  disabled={isSubmitting}
                  icon={arrowRightSvg}
                  isLoading={isSubmitting}
                  onClick={() => handleOnSubmit(formData)}
                  reverse
                  size={'medium'}
                >
                  {t('common:actions.confirm')}
                </Button>
              </ButtonWrapper>
            )}
          </Card>
        </AnimatePresence>
      </Wrapper>

      {!isMobile && (
        <ButtonWrapper>
          <AnimatePresence mode={'wait'}>
            <Button
              aria-label={t('common:actions.confirm')}
              disabled={isSubmitting}
              icon={arrowRightSvg}
              isLoading={isSubmitting}
              onClick={() => handleOnSubmit(formData)}
              reverse
              size={'medium'}
            >
              {t('common:actions.confirm')}
            </Button>
          </AnimatePresence>
        </ButtonWrapper>
      )}
    </>
  );
};
